import * as React from 'react';
import HelpDocument from '../models/helpDocument';
import HelpContent from '../components/help/help-content';
import Panel from '../components/help/panel';
import * as styles from '../components/help/help.module.scss';
import { NavLink } from 'react-router-dom';
import Paths from '../models/enums/paths';
import { datadogRum } from '@datadog/browser-rum';
import useRestService from '../shared/rest-service';
import { TreeItem, TreeData, ItemId } from '@atlaskit/tree';
import * as _ from 'lodash';
import Success from '../models/success';

/**
 * useHelp
 * @function useHelp - Custom hook for the help page
 */
const useHelp = () => {
  const [loading, setLoading] = React.useState<boolean>(false);
  const [documents, setDocuments] = React.useState<HelpDocument[]>([]);
  const [activeDocument, setActiveDocument] = React.useState<number>(-1);
  const { getAuth, postAuth, putAuth, deleteAuth } = useRestService();

  /**
   * fetchHelpDocuments
   * @function fetchHelpDocuments - Fetches the help documents from the backend and sets them to the state
   * @param clientId Id of the active client
   */
  const fetchHelpDocuments = (clientId: number) => {
    setLoading(true);
    getHelpDocuments(clientId)
      .then((documentsArray: any) => {
        setLoading(false);
        setDocuments(documentsArray);
      })
      .catch(() => setLoading(false));
  };

  /**
   * changeActiveDoc
   * @function changeActiveDoc - Changes the active document and checks if it matches the current document
   * @param params Parameters from the nav-link
   * @param location Location parameters from the nav-link
   * @param activeDoc Id of the document that the user selected
   */
  const changeActiveDoc = (params: any, location: any, activeDoc: number, updateActiveDoc: (doc: number) => void) => {
    if (params && location.state) {
      if (activeDoc === location.state.id) {
        updateActiveDoc(activeDoc);
        return true;
      }
      if (!location.state.id) return false;
    }
  };

  /**
   * renderChildren
   * @function renderChildren - Renders child panels for the table of contents
   * @param childDocs Children documents from the parent document
   * @param margin Margin for the panel
   * @param allDocs An array of all help documents
   * @param activeDoc Id of the document that the user selected
   * @param updateActiveDoc - Function to update the active document
   * @param setIsSearching Function to indicate whether the user is currently searching or not
   */
  const renderChildren = (
    childDocs: HelpDocument[],
    margin: number,
    allDocs: HelpDocument[],
    activeDoc: number,
    updateActiveDoc: (doc: number) => void,
    setIsSearching: (searching: boolean) => void
  ) => {
    return childDocs.map((doc: HelpDocument) => {
      return (
        <Panel
          key={`help-doc-${doc.snippetGroupId}`}
          parentDocument={doc}
          documents={allDocs}
          margin={margin + 20}
          activeDocument={activeDoc}
          setActiveDocument={(doc: number) => updateActiveDoc(doc)}
          setIsSearching={setIsSearching}
        />
      );
    });
  };

  /**
   * renderHelpContent
   * @function renderHelpContent - Renders the help documents
   * @param allDocuments Array of all help documents
   * @param activeDoc ID of document selected by the user
   * @returns Array of JSX elements to render the help document contents
   */
  const renderHelpContent = (allDocuments: HelpDocument[], activeDoc: number) => {
    if (allDocuments) {
      const selectedDocument = allDocuments.find(doc => doc.snippetGroupId === activeDoc);
      if (selectedDocument) {
        let currentDocument = selectedDocument;

        // Runs until root document is found
        while (currentDocument.parentGroupId !== 0) {
          currentDocument = allDocuments.find(doc => doc.snippetGroupId === currentDocument.parentGroupId);

          if (currentDocument === undefined) {
            return;
          }
        }

        const children = getChildDocsToRender(allDocuments, currentDocument, [currentDocument]);
        datadogRum.addRumGlobalContext('help', {
          group_title: children[0].title,
          selected_doc: children.find(child => child.snippetGroupId === activeDoc).title
        });

        return children.map((doc: HelpDocument, index: number) => (
          <HelpContent key={`help-doc-${doc.snippetGroupId}`} document={doc} activeDoc={activeDoc} />
        ));
      }
    }
  };

  /**
   * renderSidePanel
   * @function renderSidePanel - Renders the side panel for user to select a document
   * @param allDocuments An array of all the help documents
   * @param setIsSearching Function to indicate whether the user is currently searching or not
   * @returns Array of JSX elements to render the side panels
   */
  const renderSidePanel = (allDocuments: HelpDocument[], setIsSearching: (searching: boolean) => void) => {
    /** @const rootDocuments - Documents at the top of the tree */
    const rootDocuments = allDocuments.filter(doc => isParent(doc) && !isChild(doc));

    /** @const tree - Array of JSX elements to be rendered for the side panel  */
    const tree = rootDocuments.map(rootDocument => {
      return (
        <Panel
          key={`table-of-contents-${rootDocument.snippetGroupId}`}
          parentDocument={rootDocument}
          documents={allDocuments}
          margin={0}
          activeDocument={activeDocument}
          setActiveDocument={(doc: number) => setActiveDocument(doc)}
          setIsSearching={setIsSearching}
        />
      );
    });

    return tree;
  };

  /**
   * @function renderPreviews - Renders the preview for documents while searching
   * @param searchQuery The search query the user typed into the search bar
   * @param isSearching Boolean indicating whether or not the user is currently searching
   * @param allDocs An array of all documents
   * @param setIsSearching Function to indicate whether the user is currently searching or not
   * @returns An array of JSX elements with the preview of the documents
   */
  const renderPreviews = (
    searchQuery: string,
    isSearching: boolean,
    allDocs: HelpDocument[],
    setIsSearching: (searching: boolean) => void
  ) => {
    if (!isSearching) {
      return;
    }

    /** @const regex - Regex that uses the search query and an ignore case modifier */
    const regex = RegExp(searchQuery, 'i');

    /** @const docsToPreview - Array of documents where either the title or the content match the search query */
    const docsToPreview = allDocs.filter((doc: HelpDocument) => regex.test(doc.title) || regex.test(doc.content));

    if (docsToPreview.length === 0) {
      return <span>No Matching Documents</span>;
    }
    return docsToPreview.map((doc: HelpDocument, index: number) => (
      <NavLink
        key={`help-preview-${doc.snippetGroupId}`}
        className={styles.previewContainer}
        onClick={() => setIsSearching(false)}
        to={{
          pathname: `${Paths.Help}/${doc.snippetGroupId}`,
          state: { id: doc.snippetGroupId }
        }}
        isActive={(param, location) => {
          return changeActiveDoc(param, location, doc.snippetGroupId, setActiveDocument);
        }}
      >
        <span>{doc.title}</span>
        <div className={styles.preview}>
          <HelpContent key={`help-doc-${doc.snippetGroupId}`} document={doc} activeDoc={0} />
        </div>
      </NavLink>
    ));
  };

  /**
   * getHelpDocuments
   * @method getHelpDocuments - API call to receive the help documents
   * @param clientId - ID of the client
   */
  const getHelpDocuments = (clientId: number) => {
    return getAuth(`api/clients/${clientId}/help-documents`).then((json: any) => {
      if (json.error) {
        throw json.error;
      }

      return json;
    });
  };

  /**
   * getGlobalHelpDocuments
   * @method getGlobalHelpDocuments - API call to receive the global help documents
   */
  const getGlobalHelpDocuments = () => {
    return getAuth(`api/help-documents`).then((json: any) => {
      if (json.error) {
        throw json.error;
      }

      return json;
    });
  };

  /**
   * API call to update the sort order of the client help documents
   * @param clientId ID of the client who's documents will be updated
   * @param snippetGroupId The identifier of the document that will be updated
   * @param newParentGroupId The identifier of the parent document that it will be moved to
   * @param sourceGroupIds All the children and subchildren, along with the selected document, of the document that was moved
   * @param destinationGroupId The identifier of the next document in the list
   */
  const updateClientHelpDocumentsSortOrder = (
    clientId: number,
    snippetGroupId: number,
    newParentGroupId: number,
    sourceGroupIds: number[],
    destinationGroupId: number
  ) => {
    return putAuth(`api/clients/${clientId}/help-documents`, {
      snippetGroupId,
      newParentGroupId,
      sourceGroupIds,
      destinationGroupId
    })
      .then((json: any) => {
        if (json.error) {
          throw json.error;
        }

        return json;
      })
      .catch(error => {
        throw error;
      });
  };

  /**
   * API call to update the sort order of the global help documents
   * @param snippetGroupId The identifier of the document that will be updated
   * @param newParentGroupId The identifier of the parent document that it will be moved to
   * @param sourceGroupIds All the children and subchildren, along with the selected document, of the document that was moved
   * @param destinationGroupId The identifier of the next document in the list
   */
  const updateGlobalHelpDocumentsSortOrder = (
    snippetGroupId: number,
    newParentGroupId: number,
    sourceGroupIds: number[],
    destinationGroupId: number
  ) => {
    return putAuth(`api/help-documents`, {
      snippetGroupId,
      newParentGroupId,
      sourceGroupIds,
      destinationGroupId
    })
      .then((json: any) => {
        if (json.error) {
          throw json.error;
        }

        return json;
      })
      .catch(error => {
        throw error;
      });
  };

  /**
   * API call to receive help documents with the latest revisions
   * @param clientId - Identifier of the client who's documents we are retrieving
   */
  const getLatestHelpDocuments = (clientId: number) => {
    return getAuth(`api/clients/${clientId}/help-documents/latest-revisions`).then((json: any) => {
      if (json.error) {
        throw json.error;
      }

      return json;
    });
  };

  /**
   * API call to delete a global help document
   * @param documentId - Identifier of the document to be deleted (snippetId)
   */
  const deleteGlobalHelpDocument = (documentId: number) => {
    return deleteAuth(`api/help-documents/${documentId}`)
      .then((json: Success) => {
        return json;
      })
      .catch(error => {
        throw error;
      });
  };

  /**
   * API call to delete a client help document
   * @param clientId - Identifier of the client who's documents we are retrieving
   * @param documentId - Identifier of the document to be deleted (snippetId)
   */
  const deleteClientHelpDocument = (clientId: string, documentId: number) => {
    return deleteAuth(`api/clients/${clientId}/help-documents/${documentId}`)
      .then((json: Success) => {
        return json;
      })
      .catch(error => {
        throw error;
      });
  };

  /**
   * API call to map a global document to selected clients
   * @param clientIds - List of clients to map the global document to
   * @param documentId - Identifier of the document to be mapped (snippetGroupId)
   */
  const mapGlobalDocument = (clientIds: number[], documentId: number) => {
    return putAuth(`api/help-documents/${documentId}/map`, { clientIds })
      .then((json: Success) => {
        return json;
      })
      .catch(error => {
        throw error;
      });
  };

  /**
   * API call to map a global document to selected clients
   * @param clientId - Unique identifier of the client
   * @param documentIds - List of documents that will be mapped to a given client(snippetGroupId)
   */
  const mapGlobalDocumentsToClients = (clientId: number, snippetGroupIds: number[]) => {
    return postAuth(`api/clients/${clientId}/help-documents/map`, { snippetGroupIds })
      .then((json: Success) => {
        return json;
      })
      .catch(error => {
        throw error;
      });
  };

  /**
   * isParent
   * @method isParent - Checks to see if document is a parent document
   * @param doc - Document to be checked
   * @returns TRUE if snippetGroupId is not 0,
   * FALSE if snippetGroupId is 0
   */
  const isParent = (doc: HelpDocument) => doc.snippetGroupId !== 0;

  /**
   * isChild
   * @method isChild - Checks to see if document is a child document
   * @param doc - Document to be checked
   * @returns TRUE if parentGroupId is not 0,
   * FALSE if parentGroupId is 0
   */
  const isChild = (doc: HelpDocument) => doc.parentGroupId !== 0;

  /**
   * isActiveDoc
   * @method isActiveDoc - Checks if the document is active
   * @param parentDocument Document to be checked
   * @param activeDocuments Array of all active documents
   * @returns TRUE if the document is active, FALSE otherwise
   */
  const isActiveDoc = (parentDocument: HelpDocument, activeDocuments: HelpDocument[]) => {
    return activeDocuments.some(doc => parentDocument.snippetGroupId === doc.snippetGroupId);
  };

  /**
   * Gets all the documents without the root document
   * @param tree - The Atlassian Tree object containing all the documents
   */
  const documentsWithoutRoot = (tree: TreeData) =>
    Object.values(Object.values(tree)[1]).filter((doc: TreeItem) => doc.id !== '-1') as TreeItem[];

  /**
   * Gets all the documents, includes the root document
   * @param tree - The Atlassian Tree object containing all the documents
   */
  const documentsWithRoot = (tree: TreeData) => Object.values(Object.values(tree)[1]) as TreeItem[];

  const updateDocumentTreeAfterDelete = (prevDocumentTree: TreeData, deletedDocId: number) => {
    const documentTree = _.cloneDeep(prevDocumentTree);
    const deletedDoc = documentsWithoutRoot(prevDocumentTree).find(doc => doc.data.snippetId === deletedDocId);
    const children = deletedDoc.children as ItemId[];

    const parent = documentsWithRoot(prevDocumentTree).find((doc: TreeItem) => doc.children.includes(deletedDoc.id));

    const deletedDocIndex = parent.children.indexOf(deletedDoc.data.snippetGroupId);

    // Remove deleted document from the list of the parents children, and insert deleted documents children
    if (deletedDocIndex !== -1) {
      documentTree.items[parent.id].children.splice(deletedDocIndex, 1, ...children);
    }

    // If the parent no longer has any children, set hasChildren and isExpanded to false
    documentTree.items[parent.id].hasChildren = documentTree.items[parent.id].children.length > 0;
    documentTree.items[parent.id].isExpanded = documentTree.items[parent.id].children.length > 0;

    delete documentTree.items[`${deletedDoc.data.snippetGroupId}`];

    return documentTree;
  };

  const updateDocumentTreeAfterFetch = (prevTree: TreeData, newDocuments: HelpDocument[]) => {
    const newTree = _.cloneDeep(prevTree);
    const prevHelpDocuments = documentsWithoutRoot(prevTree).map(document => document.data);
    const newDocumentsIds = newDocuments.map(document => document.snippetGroupId);

    prevHelpDocuments.forEach(document => {
      if (newDocumentsIds.includes(document.snippetGroupId as number)) {
        const childrenList = newDocuments
          .filter(doc => doc.parentGroupId === document.snippetGroupId)
          .map(doc => doc.snippetGroupId);
        newTree.items[`${document.snippetGroupId}`].data = newDocuments.find(
          doc => doc.snippetGroupId === document.snippetGroupId
        );
        newTree.items[`${document.snippetGroupId}`].hasChildren = childrenList.length > 0;
        newTree.items[`${document.snippetGroupId}`].children = childrenList;
      } else {
        delete newTree.items[`${document.snippetGroupId}`];
      }
    });

    newDocuments.forEach(document => {
      if (!newTree.items[`${document.snippetGroupId}`]) {
        const childrenList = newDocuments
          .filter(doc => doc.parentGroupId === document.snippetGroupId)
          .map(doc => doc.snippetGroupId);
        const newItem = {
          id: document.snippetGroupId,
          data: document,
          children: childrenList,
          hasChildren: childrenList.length > 0,
          isExpanded: true,
          isChildrenLoading: false
        };
        newTree.items[`${document.snippetGroupId}`] = newItem;
      }
    });

    const rootDocuments = newDocuments.filter(doc => doc.parentGroupId === 0).map(doc => doc.snippetGroupId);

    newTree.items['-1'].hasChildren = rootDocuments.length > 0;
    newTree.items['-1'].children = rootDocuments;
    return newTree;
  };

  /**
   * Finds all the children and subchildren of the selected document
   * @param id - Identifier of the document to start the search from
   * @param idList - Array of child document ID's
   * @param newTree - The Atlassian Tree object containing all the documents
   */
  const findChildren = (id: number, idList: number[], newTree: TreeData) => {
    let arr = [id];

    if (idList.length < 1) {
      return arr;
    }

    /** Goes through all the help documents and finds all the children, and does so repeatedly */
    idList.forEach(doc => {
      const foundDocument = documentsWithoutRoot(newTree).find(d => d.data.snippetGroupId === doc);
      arr = [
        ...arr,
        ...findChildren(foundDocument.data.snippetGroupId, [...(foundDocument.children as number[])], newTree)
      ];
    });
    return arr;
  };

  /**
   * Finds the top level parent document for the document selected
   * @param documentId - Identifier of the document to start from
   * @param tree - The Atlassian Tree object containing all the documents
   */
  const findTopLevelDocument = (documentId: number, tree: TreeData) => {
    const parent = documentsWithRoot(tree).find(doc => {
      return doc.children.includes(documentId);
    });

    if (!parent.data) {
      return documentId;
    }
    return findTopLevelDocument(parent.data.snippetGroupId, tree);
  };

  /**
   * Finds the document that's after the selected document
   * @param parentId - The identifier of the documents parent
   * @param index - The index of the current document
   * @param tree - The Atlassian Tree object containing all the documents
   */
  const findNextDocument = (parentId: number, index: number | undefined, tree: TreeData) => {
    const parent = documentsWithRoot(tree).find((doc: TreeItem) => {
      if (parentId === 0 || parentId === -1) {
        return doc.id === '-1';
      }
      return doc.data.snippetGroupId === parentId;
    });
    const grandParent = documentsWithRoot(tree).find((doc: TreeItem) => {
      if (!parent.data || parent.data.snippetGroupId === 0 || !doc.data) {
        return doc.id === '-1';
      }
      return doc.children.includes(parent.id);
    });
    if (index === undefined) {
      return findNextDocument(
        Number(grandParent.id),
        grandParent.children.findIndex(child => child === parent.data.snippetGroupId),
        tree
      );
    }
    let nextDocument = parent.children[index + 1];
    if (nextDocument) {
      return nextDocument;
    }
    if (!parent.data) {
      return -1;
    }
    return findNextDocument(
      Number(grandParent.id),
      grandParent.children.findIndex(child => child === parent.data.snippetGroupId),
      tree
    );
  };

  /**
   * Parent of the dragged document
   * @param parentId - ID of the new parent
   * @param tree - The Atlassian Tree object containing all the documents
   */
  const parentOfDocumentDragged = (parentId: number, tree: TreeData) => {
    return documentsWithRoot(tree).find((doc: TreeItem) => {
      if (parentId === 0) {
        return doc.id === '-1';
      }
      return doc.data.snippetGroupId === parentId;
    });
  };

  /**
   * The snippetGroupId of the dragged document
   * @param index - Index where the document was dragged to
   * @param parentId - ID of the new parent
   * @param tree - The Atlassian Tree object containing all the documents
   */
  const draggedSnippetGroupId = (index: number | undefined, parentId: number, tree: TreeData) => {
    return parentOfDocumentDragged(parentId, tree).children[
      index !== undefined ? index : parentOfDocumentDragged(parentId, tree).children.length - 1
    ] as number;
  };

  /**
   * The object for the dragged document
   */
  const documentDragged = (documentId: number, tree: TreeData) => {
    return documentsWithoutRoot(tree).find(doc => doc.data.snippetGroupId === documentId);
  };

  /**
   * isTopLevelDoc
   * @method isTopLevelDoc - Checks if the document is a root level document
   * @param parentDocument Document to be checked
   * @returns TRUE if the document is a root level document, FALSE otherwise
   */
  const isTopLevelDoc = (parentDocument: HelpDocument) => parentDocument.parentGroupId === 0;

  /**
   * getChildDocsToRender
   * @method getChildDocsToRender Creates an array of documents to render from the root doc to all it's children
   * @param allDocuments An array of all help documents
   * @param rootDoc The document at the top of the tree from the document that was selected by the user
   * @param docsToRender An array of the documents that will be rendered
   * @returns The array of documents that will be rendered
   */
  const getChildDocsToRender = (allDocuments: HelpDocument[], rootDoc: HelpDocument, docsToRender: HelpDocument[]) => {
    const children = allDocuments.filter(doc => doc.parentGroupId === rootDoc.snippetGroupId);

    // If there are children, push the children into the array, and then call this function
    if (children.length > 0) {
      children.forEach(doc => {
        docsToRender.push(doc);
        return getChildDocsToRender(allDocuments, doc, docsToRender);
      });
    }

    /** If there are no more children, push the document into the array and return the array */
    if (children.length === 0) {
      children.forEach(doc => docsToRender.push(doc));
    }

    return docsToRender;
  };

  /**
   * Creates an array of documents to render from the root doc to all it's children
   * @param documentId ID of the selected document
   * @param tree The tree object of documents
   * @returns The array of documents that will be rendered
   */
  const getChildDocsToRenderEdit = (documentId: number, tree: TreeData) => {
    const topLevelDocumentId = findTopLevelDocument(documentId, tree);
    const selectedDocument = documentDragged(topLevelDocumentId, tree);
    const childrenArray = findChildren(topLevelDocumentId, [...selectedDocument.children] as number[], tree);

    return documentsWithoutRoot(tree)
      .filter(doc => childrenArray.includes(doc.data.snippetGroupId))
      .map(doc => doc.data)
      .sort((a, b) =>
        childrenArray.indexOf(a.snippetGroupId) > childrenArray.indexOf(b.snippetGroupId) ? 1 : -1
      ) as HelpDocument[];
  };

  return {
    loading,
    documents,
    getHelpDocuments,
    fetchHelpDocuments,
    activeDocument,
    setActiveDocument,
    changeActiveDoc,
    renderChildren,
    renderHelpContent,
    renderSidePanel,
    renderPreviews,
    documentsWithoutRoot,
    getChildDocsToRender,
    isActiveDoc,
    isTopLevelDoc,
    getGlobalHelpDocuments,
    updateDocumentTreeAfterFetch,
    getLatestHelpDocuments,
    deleteGlobalHelpDocument,
    updateDocumentTreeAfterDelete,
    deleteClientHelpDocument,
    updateClientHelpDocumentsSortOrder,
    updateGlobalHelpDocumentsSortOrder,
    getChildDocsToRenderEdit,
    findNextDocument,
    draggedSnippetGroupId,
    findChildren,
    mapGlobalDocumentsToClients,
    mapGlobalDocument,
    documentsWithRoot
  };
};

export default useHelp;
